import {
  Form,
  useFormConfig,
  FormGrid,
  MaxWidthContent,
  PageWithSidebar,
} from "@endya-conformite/ui-kit";
import {
  GatewayEntreprise,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import { AdresseInformation } from "@src/societes/societe/informations/sections/AdresseInformation";
import { ContactMediationInformation } from "@src/societes/societe/informations/sections/ContactMediationInformation";
import { GeneralInformation } from "@src/societes/societe/informations/sections/GeneralInformation";
import { useEntreprises } from "@src/store/store.entreprise";
import { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { SocietePaiement } from "../paiementCategories/SocietePaiement";
import styles from "./SocieteInformation.module.scss";
import { SocieteInformationSidebar } from "./SocieteInformationSidebar";
import {
  SocieteInformationFormValues,
  useSocieteInformation,
} from "./useSocieteInformation";

export const ID_FORM_SOCIETE_INFORMATION = "id-form-societe-information";

type SocieteInformationProps = {
  societe: GatewayEntreprise.EntrepriseAControllerResponseType;
  setEntreprise: (
    entreprise: GatewayEntreprise.EntrepriseAControllerResponseType
  ) => void;
  sectionRefs: React.MutableRefObject<{
    general?: HTMLDivElement | null;
    adresse?: HTMLDivElement | null;
    contact?: HTMLDivElement | null;
  }>;
  setActiveSection: (section: "general" | "adresse" | "contact" | null) => void;
};

function SocieteInformation({
  societe,
  setEntreprise,
  sectionRefs,
  setActiveSection,
}: SocieteInformationProps) {
  const { schema, handleSubmit, isLoading, payment } = useSocieteInformation(
    societe,
    setEntreprise
  );

  const {
    hubspotId,
    raisonSociale,
    adresse,
    siren,
    formeJuridique,
    trancheEffectif,
    categoriesActivites,
    categoriesActivitesAccessoires,
    consentementEDI,
    sousCategorieCOBSP,
    sousCategoriesIOBSP,
    nomMediation,
    prenomMediation,
    mailMediation,
    telephoneMediation,
    civiliteMediation,
  } = societe;

  const formConfig = useFormConfig<typeof schema, SocieteInformationFormValues>(
    {
      schema,
      defaultValues: {
        ...adresse,
        complementAdresse: adresse.complementAdresse ?? "",
        id: hubspotId,
        raisonSociale,
        siren,
        formeJuridique,
        trancheEffectif,
        categories: [...categoriesActivites, ...categoriesActivitesAccessoires],
        coaAccessoire: categoriesActivitesAccessoires.includes(
          GatewayInfoSocieteOrias.Categorie.COA
        ),
        cobspAccessoire: categoriesActivitesAccessoires.includes(
          GatewayInfoSocieteOrias.Categorie.COBSP
        ),
        miaAccessoire: categoriesActivitesAccessoires.includes(
          GatewayInfoSocieteOrias.Categorie.MIA
        ),
        miobspAccessoire: categoriesActivitesAccessoires.includes(
          GatewayInfoSocieteOrias.Categorie.MIOBSP
        ),
        needCOBSP: categoriesActivites.includes(
          GatewayInfoSocieteOrias.Categorie.COBSP
        ),
        needMIOBSP: categoriesActivites.includes(
          GatewayInfoSocieteOrias.Categorie.MIOBSP
        ),
        sousCategoriesCOBSP: sousCategorieCOBSP,
        sousCategoriesMIOBSP: sousCategoriesIOBSP,
        consentementEDI,
        nomMediation,
        prenomMediation,
        mailMediation,
        telephoneMediation,
        civiliteMediation,
      },
    }
  );

  const categoriesWatched = formConfig.watch("categories");
  const [needCOBSP, needMIOBSP] = formConfig.watch(["needCOBSP", "needMIOBSP"]);

  useEffect(() => {
    formConfig.setValue(
      "needCOBSP",
      categoriesWatched?.includes(GatewayInfoSocieteOrias.Categorie.COBSP)
    );
    formConfig.setValue(
      "needMIOBSP",
      categoriesWatched?.includes(GatewayInfoSocieteOrias.Categorie.MIOBSP)
    );
  }, [needCOBSP, needMIOBSP, categoriesWatched, formConfig]);

  useEffect(() => {
    const order = ["general", "adresse", "contact"] as const;

    const compute = () => {
      const { scrollY } = window;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const atBottom = Math.ceil(scrollY + windowHeight) >= docHeight - 2;

      if (atBottom && scrollY > 0) {
        setActiveSection("contact");
        return;
      }

      const threshold = windowHeight * 0.2;
      const active = order.reduce<(typeof order)[number]>((current, key) => {
        const ref = sectionRefs.current[key];
        if (ref && ref.getBoundingClientRect().top <= threshold) return key;
        return current;
      }, "general");
      setActiveSection(active);
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [setActiveSection, sectionRefs]);

  if (payment.paiementIntention?.paiement && payment.newEntreprise)
    return (
      <SocietePaiement
        siren={societe.siren}
        paiementIntention={{
          ...payment.paiementIntention,
          paiement: payment.paiementIntention.paiement,
          idsEntreprisesNonAdherentes: [],
        }}
        entreprise={payment.newEntreprise}
      />
    );

  return (
    <FormGrid>
      <Form
        formConfig={formConfig}
        onSubmit={handleSubmit}
        id={ID_FORM_SOCIETE_INFORMATION}
      >
        <div
          id="general"
          ref={(el) => {
            sectionRefs.current.general = el;
          }}
        >
          <GeneralInformation
            categories={[
              ...categoriesActivites,
              ...categoriesActivitesAccessoires,
            ]}
            categoriesWatched={categoriesWatched}
            needCOBSP={needCOBSP}
            needMIOBSP={needMIOBSP}
          />
        </div>

        <div
          id="adresse"
          ref={(el) => {
            sectionRefs.current.adresse = el;
          }}
        >
          <AdresseInformation
            categories={[
              ...categoriesActivites,
              ...categoriesActivitesAccessoires,
            ]}
            formState={{
              isDirty: formConfig.formState.isDirty,
            }}
            isLoading={isLoading}
            onCancel={formConfig.reset}
          />
        </div>

        <div
          id="contact"
          ref={(el) => {
            sectionRefs.current.contact = el;
          }}
        >
          <ContactMediationInformation nomMediationPlaceholder={nomMediation} />
        </div>
      </Form>
    </FormGrid>
  );
}

export function SocieteInformationContainer() {
  const { entreprisesBySiren, setEntreprise } = useEntreprises();
  const { id } = useParams<{ id: string }>();
  const societe = id && entreprisesBySiren ? entreprisesBySiren[id] : undefined;

  const sectionRefs = useRef({});
  const clickedAtRef = useRef<number>(0);
  const [activeSection, setActiveSection] = useState<
    "general" | "adresse" | "contact" | null
  >(null);

  const setActiveFromObserver = useCallback(
    (section: "general" | "adresse" | "contact" | null) => {
      if (performance.now() - clickedAtRef.current < 800) return;
      setActiveSection(section);
    },
    []
  );

  const setActiveFromClick = useCallback(
    (section: "general" | "adresse" | "contact" | null) => {
      clickedAtRef.current = performance.now();
      setActiveSection(section);
    },
    []
  );

  return (
    <PageWithSidebar
      sidebar={
        <SocieteInformationSidebar
          sectionRefs={sectionRefs}
          activeSection={activeSection}
          setActiveSection={setActiveFromClick}
        />
      }
    >
      <MaxWidthContent className={styles.page} size="big">
        {societe && (
          <SocieteInformation
            societe={societe}
            setEntreprise={setEntreprise}
            sectionRefs={sectionRefs}
            setActiveSection={setActiveFromObserver}
          />
        )}
      </MaxWidthContent>
    </PageWithSidebar>
  );
}
